import React, { useState, useEffect, useCallback } from 'react';
import { useRoleLabels } from '../contexts/RoleLabelsContext';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '../hooks/useMobile';
import Sidebar from '../components/Sidebar';
import AppDownloadBanner from '../components/AppDownloadBanner';

const token = () => localStorage.getItem('token');
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtAgo = (iso) => {
  const diff = Math.floor((new Date() - new Date(iso)) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return fmt(iso);
};
const fmtCcy = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// ── Design tokens (match BrokerConnect) ────────────────────────────────────
const NAVY   = '#0e1b2c';
const GREEN  = '#10b981';
const GREEN2 = '#059669';
const BORDER = '#E2E8F0';
const MUTED  = '#9ca3af';
const TEXT   = '#374151';

// ── Level config ───────────────────────────────────────────────────────────
const LEVEL_CONFIG = {
  company: { label: 'Introducer Company', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: '🏢' },
  manager: { label: 'Introducer Manager', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', icon: '👔' },
  adviser: { label: 'Introducer Adviser', color: '#0f766e', bg: '#f0fdfa', border: '#99f6e4', icon: '👤' },
  solo:    { label: 'Solo Introducer', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '⭐' },
};

const RAG = {
  red:   { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', dot: '#ef4444' },
  amber: { bg: '#fef3c7', border: '#fcd34d', color: '#92400e', dot: '#f59e0b' },
  green: { bg: '#d1fae5', border: '#6ee7b7', color: '#065f46', dot: '#10b981' },
};

const STATUS_CONFIG = {
  submitted: { label: 'Pending Review', bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: '⏳' },
  approved:  { label: 'Approved',       bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', icon: '✓' },
  rejected:  { label: 'Rejected',       bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: '✕' },
  converted: { label: 'Deal Created',   bg: '#dbeafe', color: '#1e40af', border: '#93c5fd', icon: '🎉' },
};

// ── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, icon }) {
  return (
    <div style={{
      background: accent ? NAVY : '#fff',
      border: accent ? 'none' : `1px solid ${BORDER}`,
      borderRadius: 12,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      {icon && <span style={{ fontSize: 16, marginBottom: 0 }}>{icon}</span>}
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ? GREEN : NAVY, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: accent ? '#f8fafc' : TEXT, marginTop: 2 }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: accent ? '#94a3b8' : MUTED, marginTop: 1 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Role Badge ─────────────────────────────────────────────────────────────
function RoleBadge({ level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.solo;
  const { labelForTier } = useRoleLabels();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      borderRadius: 20, padding: '4px 12px',
      fontSize: 12, fontWeight: 600,
    }}>
      {cfg.icon} {labelForTier('introducer', level)}
    </span>
  );
}

// ── Broker Panel ───────────────────────────────────────────────────────────
function BrokerPanel({ company, navigate }) {
  if (!company) return null;
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${BORDER}`,
      borderRadius: 12,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'linear-gradient(135deg, #0e1b2c, #1e3a5f)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 17, flexShrink: 0,
      }}>
        🏦
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
          Introducing for
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
          {company.broker_name || 'Your Broker'}
        </div>
        {company.name && company.name !== company.broker_name && (
          <div style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>
            Via: {company.name}
          </div>
        )}
      </div>
      {company.fee_type && company.fee_type !== 'none' && (
        <div style={{
          textAlign: 'right', flexShrink: 0,
          padding: '8px 14px',
          background: '#f8fafc',
          borderRadius: 8,
          border: `1px solid ${BORDER}`,
        }}>
          <div style={{ fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fee</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginTop: 2 }}>
            {company.fee_type === 'fixed' && company.fixed_fee
              ? fmtCcy(company.fixed_fee)
              : company.fee_type === 'percentage' && company.fee_percentage
              ? `${company.fee_percentage}%`
              : company.fee_type}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Team Summary Panel ─────────────────────────────────────────────────────
function TeamSummaryPanel({ company, level, navigate }) {
  if (!company || level === 'adviser') return null;
  if (!company.manager_count && !company.adviser_count) return null;

  const isCompany = level === 'company';
  const isManager = level === 'manager';

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${BORDER}`,
      borderRadius: 12,
      overflow: 'hidden',
      marginTop: 10,
    }}>
      <div style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: NAVY }}>
          👥 Your Team
        </h2>
        <button
          onClick={() => navigate('/dashboard/introducer-team')}
          style={{
            background: 'none', border: `1px solid ${BORDER}`,
            borderRadius: 6, padding: '4px 12px',
            fontSize: 12, fontWeight: 600, color: TEXT,
            cursor: 'pointer',
          }}
        >
          Manage →
        </button>
      </div>
      <div style={{ padding: '10px 14px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {isCompany && (
          <div style={{ flex: 1, minWidth: 100, textAlign: 'center', padding: '8px', background: '#eff6ff', borderRadius: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8' }}>{company.manager_count || 0}</div>
            <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600, marginTop: 2 }}>Manager{company.manager_count !== 1 ? 's' : ''}</div>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 100, textAlign: 'center', padding: '8px', background: '#f0fdfa', borderRadius: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f766e' }}>{company.adviser_count || 0}</div>
          <div style={{ fontSize: 12, color: '#14b8a6', fontWeight: 600, marginTop: 2 }}>Adviser{company.adviser_count !== 1 ? 's' : ''}</div>
        </div>
        {(company.manager_count === 0 && company.adviser_count === 0) && (
          <div style={{ flex: '0 0 100%', textAlign: 'center', fontSize: 12, color: MUTED, paddingTop: 4 }}>
            Add team members to grow your referral network
          </div>
        )}
      </div>
    </div>
  );
}

// ── Activity Feed ──────────────────────────────────────────────────────────
function ActivityFeed({ items }) {
  if (!items.length) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: MUTED, fontSize: 13 }}>
        No recent activity
      </div>
    );
  }
  return (
    <div>
      {items.map((item, i) => {
        const isPayment = item.type === 'commission_paid';
        const rag = RAG[item.stageColour] || null;
        return (
          <div key={`${item.type}-${item.dealId}-${i}`} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '8px 0',
            borderBottom: i < items.length - 1 ? `1px solid #f1f5f9` : 'none',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isPayment ? '#d1fae5' : '#eff6ff', fontSize: 15,
            }}>
              {isPayment ? '💰' : '📋'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 3 }}>
                {item.clientName}
              </div>
              {isPayment ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, fontWeight: 600, color: '#065f46', background: '#d1fae5',
                  padding: '2px 8px', borderRadius: 12,
                }}>
                  Commission paid · {fmtCcy(item.amount)}
                </div>
              ) : rag ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 600,
                  color: rag.color, background: rag.bg,
                  border: `1px solid ${rag.border}`,
                  padding: '2px 8px', borderRadius: 12,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: rag.dot, flexShrink: 0 }} />
                  {item.stageName}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#6b7280' }}>Stage: {item.stageName}</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: MUTED, whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2 }}>
              {fmtAgo(item.at)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Lead Card ──────────────────────────────────────────────────────────────
// Simplified: clicking navigates to /introducer/referrals?open=<id>
function LeadCard({ lead, onView }) {
  const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.submitted;
  return (
    <div
      onClick={onView}
      style={{
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderLeft: `4px solid ${sc.border}`,
        borderRadius: 10,
        marginBottom: 6,
        padding: '10px 14px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: NAVY, marginBottom: 2 }}>{lead.client_name}</div>
        <div style={{ fontSize: 11, color: MUTED }}>{lead.client_email}</div>
        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Submitted {fmt(lead.created_at)}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
          {sc.icon} {sc.label}
        </span>
        <span style={{ color: MUTED, fontSize: 16 }}>›</span>
      </div>
    </div>
  );
}

// ── Quick Nav Card ─────────────────────────────────────────────────────────
function QuickNavCard({ items, navigate }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
      padding: '10px', display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
    }}>
      {items.map((item) => (
        <button
          key={item.path}
          onClick={() => navigate(item.path)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
            padding: '10px 12px', background: '#f8fafc', border: `1px solid #f1f5f9`,
            borderRadius: 8, cursor: 'pointer', textAlign: 'left',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
          onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
        >
          <span style={{
            width: 28, height: 28, borderRadius: 7,
            background: item.iconBg || '#f1f5f9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, flexShrink: 0,
          }}>
            {item.icon}
          </span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: NAVY }}>{item.label}</div>
            {item.sub && <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>{item.sub}</div>}
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function IntroducerDashboard() {
  const mobile   = useMobile();
  const navigate = useNavigate();

  const [user, setUser]         = useState(null);
  const [stats, setStats]       = useState(null);
  const [activity, setActivity] = useState([]);
  const [leads, setLeads]       = useState([]);
  const [company, setCompany]   = useState(null);
  const [leadTab, setLeadTab]   = useState('all');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [lastLogin, setLastLogin]   = useState(null);
  const [sinceStats, setSinceStats] = useState(null);
  const [celebrationDismissed, setCelebrationDismissed] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showUpdates, setShowUpdates] = useState(false);

  const loadData = useCallback(async (userData, lastLoginIso) => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token()}` };

      const [dashRes, leadsRes, companyRes, sinceRes, announcementsRes] = await Promise.all([
        fetch('/api/introducer/dashboard',  { headers }),
        fetch('/api/introducer-leads',      { headers }),
        fetch('/api/introducer/company',    { headers }),
        lastLoginIso
          ? fetch(`/api/summary/since-last-login?since=${encodeURIComponent(lastLoginIso)}`, { headers })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        fetch('/api/introducer/announcements', { headers }).catch(() => null),
      ]);

      if (dashRes.ok) {
        const d = await dashRes.json();
        setStats(d.stats || {});
        setActivity(d.recentActivity || []);
      } else {
        setError('Failed to load dashboard data');
      }
      if (leadsRes.ok) {
        const d = await leadsRes.json();
        setLeads(d.leads || []);
      }
      if (companyRes.ok) {
        setCompany(await companyRes.json());
      }
      if (sinceRes) setSinceStats(sinceRes);
      if (announcementsRes && announcementsRes.ok) {
        const ad = await announcementsRes.json();
        setAnnouncements(ad.announcements || []);
      }
    } catch {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    const ll = localStorage.getItem('lastLoginAt');
    if (ll) setLastLogin(new Date(ll));
    localStorage.setItem('lastLoginAt', new Date().toISOString());
    loadData(userData, ll);
  }, [loadData]);

  // Broker announcements: one-way broadcast, "dismiss" = mark read on open.
  const markAnnouncementRead = useCallback(async (id) => {
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, read_at: a.read_at || new Date().toISOString() } : a));
    try {
      await fetch(`/api/introducer/announcements/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}` },
      });
    } catch { /* optimistic update already applied */ }
  }, []);

  const level = user?.introducerLevel || user?.introducer_level || 'solo';
  const LEAD_TABS = ['all', 'submitted', 'approved', 'converted', 'rejected'];
  const filteredLeads = leadTab === 'all' ? leads : leads.filter(l => l.status === leadTab);
  const leadCounts = LEAD_TABS.reduce((acc, t) => {
    acc[t] = t === 'all' ? leads.length : leads.filter(l => l.status === t).length;
    return acc;
  }, {});

  const hasSince = sinceStats && (sinceStats.newLeads > 0 || sinceStats.dealsUpdated > 0 || sinceStats.commissionsPaid > 0);

  const fmtLoginDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  const isManagerOrAbove = ['company', 'manager', 'solo'].includes(level);

  const quickNavItems = [
    { label: 'My Referrals',       icon: '🔗', iconBg: '#eff6ff', path: '/introducer/referrals',     sub: 'Track all referrals & deals' },
    { label: 'Commission Summary', icon: '💷', iconBg: '#f0fdf4', path: '/introducer/commissions',  sub: 'Earnings & payments' },
    { label: 'Analytics',         icon: '📈', iconBg: '#faf5ff', path: '/introducer-analytics',    sub: 'Performance metrics' },
    ...(isManagerOrAbove
      ? [{ label: 'Manage Team', icon: '👥', iconBg: '#fff7ed', path: '/dashboard/introducer-team', sub: 'Add & manage team members' }]
      : []),
  ];

  const ml = mobile ? 0 : 70;

  if (loading) {
    return (
      <>
        <AppDownloadBanner />
      <Sidebar />
        <div style={{ marginLeft: ml, padding: 32, color: MUTED, fontSize: 14 }}>Loading…</div>
      </>
    );
  }

  return (
    <>
      <AppDownloadBanner />
      <Sidebar />

      {/* Since-last-login banner */}
      {lastLogin && (
        <div style={{
          marginLeft: ml,
          background: '#f0fdf4', borderBottom: '1px solid #bbf7d0',
          padding: '8px 24px', fontSize: 12, color: '#166534',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          <span>Last login: {fmtLoginDate(lastLogin)}</span>
          {hasSince && (
            <span style={{ display: 'flex', gap: 16 }}>
              {sinceStats.newLeads > 0 && (
                <span>📥 <strong>{sinceStats.newLeads}</strong> new lead{sinceStats.newLeads !== 1 ? 's' : ''}</span>
              )}
              {sinceStats.dealsUpdated > 0 && (
                <span>✏️ <strong>{sinceStats.dealsUpdated}</strong> deal{sinceStats.dealsUpdated !== 1 ? 's' : ''} updated</span>
              )}
              {sinceStats.commissionsPaid > 0 && (
                <span>💰 <strong>{sinceStats.commissionsPaid}</strong> commission{sinceStats.commissionsPaid !== 1 ? 's' : ''} paid</span>
              )}
            </span>
          )}
        </div>
      )}

      <div style={{ marginLeft: ml, minHeight: '100vh', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: mobile ? '16px 14px' : '20px 24px' }}>

          {/* ── Page Header ── */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: NAVY }}>
                    Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
                  </h1>
                  <RoleBadge level={level} />
                </div>
                <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>
                  Here's what's happening with your pipeline
                </p>
              </div>
              <button
                onClick={() => navigate('/submit-lead')}
                style={{
                  background: `linear-gradient(135deg, ${GREEN}, ${GREEN2})`,
                  color: '#fff', border: 'none',
                  borderRadius: 10, padding: '14px 28px',
                  fontWeight: 700, fontSize: 15, cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                📝 Submit a Lead
              </button>
            </div>
          </div>

          {/* ── Updates alert strip (broker announcements) ── */}
          {announcements.filter(a => !a.read_at).length > 0 && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
              <div
                onClick={() => setShowUpdates(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }}
              >
                <span style={{ fontSize: 18 }}>📢</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1e40af' }}>
                  {announcements.filter(a => !a.read_at).length} new update{announcements.filter(a => !a.read_at).length !== 1 ? 's' : ''} from your broker
                </span>
                <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>{showUpdates ? 'Hide' : 'View'}</span>
              </div>
              {showUpdates && (
                <div style={{ borderTop: '1px solid #bfdbfe' }}>
                  {announcements.map(a => (
                    <div
                      key={a.id}
                      onClick={() => markAnnouncementRead(a.id)}
                      style={{
                        padding: '12px 16px', cursor: 'pointer',
                        background: a.read_at ? '#fff' : '#f0f7ff',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontWeight: a.read_at ? 500 : 700, fontSize: 13, color: '#0e1b2c' }}>{a.title}</span>
                        <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{a.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* ── Broker Panel ── */}
          <BrokerPanel company={company} navigate={navigate} />

          {/* ── Commission celebration ── */}
          {!celebrationDismissed && sinceStats?.commissionsPaid > 0 && (
            <div style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, #14532d 100%)`,
              borderRadius: 12, padding: '14px 18px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 34, flexShrink: 0 }}>🎉</span>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 3 }}>
                  {sinceStats.commissionsPaid === 1 ? 'A commission was paid' : `${sinceStats.commissionsPaid} commissions were paid`} since your last visit
                </div>
                <div style={{ color: '#86efac', fontSize: 13, lineHeight: 1.5 }}>
                  That's {fmtCcy(stats?.commissionYtd)} earned this year through your referrals.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => navigate('/introducer/commissions')} style={{
                  background: GREEN, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  View earnings →
                </button>
                <button onClick={() => setCelebrationDismissed(true)} title="Dismiss" style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                  fontSize: 18, cursor: 'pointer', padding: 4,
                }}>×</button>
              </div>
            </div>
          )}

          {/* ── Stat Cards ── */}
          {stats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: 10,
              marginBottom: 16,
            }}>
              <StatCard
                label="Commission This Month"
                value={fmtCcy(stats.commissionThisMonth)}
                sub={stats.dealsPaidThisMonth > 0 ? `${stats.dealsPaidThisMonth} deal${stats.dealsPaidThisMonth !== 1 ? 's' : ''} paid` : 'No payments yet'}
                accent
              />
              <StatCard label="Pipeline Value"   value={fmtCcy(stats.commissionPipeline)} sub="Expected commission" />
              <StatCard label="Active Deals"     value={stats.activeDeals} sub={stats.dealsThisMonth > 0 ? `+${stats.dealsThisMonth} this month` : 'In progress'} />
              <StatCard label="Paid Commission YTD" value={fmtCcy(stats.commissionYtd)} sub="Paid this year" />
            </div>
          )}

          {/* ── Main 2-col grid ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : '1fr 320px',
            gap: 14,
            alignItems: 'start',
          }}>
            {/* Left: Leads */}
            <div>
              <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px 0', borderBottom: `1px solid #f1f5f9` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: NAVY }}>
                      Submitted Leads
                      <span style={{
                        marginLeft: 8, fontSize: 12, fontWeight: 600,
                        background: '#f1f5f9', color: '#64748b',
                        padding: '2px 8px', borderRadius: 12,
                      }}>
                        {leads.length}
                      </span>
                    </h2>
                    <button
                      onClick={() => navigate('/introducer/referrals')}
                      style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      View all →
                    </button>
                  </div>
                  {/* Tabs */}
                  <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                    {[
                      { key: 'all',       label: 'All',       color: TEXT },
                      { key: 'submitted', label: 'Pending',   color: '#d97706' },
                      { key: 'approved',  label: 'Approved',  color: GREEN },
                      { key: 'converted', label: 'Converted', color: '#3b82f6' },
                      { key: 'rejected',  label: 'Rejected',  color: '#ef4444' },
                    ].filter(t => t.key === 'all' || leadCounts[t.key] > 0).map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setLeadTab(tab.key)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '6px 14px',
                          borderBottom: leadTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
                          marginBottom: -1,
                          fontSize: 12, fontWeight: leadTab === tab.key ? 600 : 500,
                          color: leadTab === tab.key ? tab.color : MUTED,
                          display: 'flex', alignItems: 'center', gap: 5,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tab.label}
                        {leadCounts[tab.key] > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            background: tab.color, color: '#fff',
                            padding: '1px 5px', borderRadius: 10,
                          }}>
                            {leadCounts[tab.key]}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ padding: '10px 16px' }}>
                  {filteredLeads.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: MUTED, fontSize: 13 }}>
                      {leads.length === 0 ? (
                        <>
                          No leads yet.{' '}
                          <button
                            onClick={() => navigate('/submit-lead')}
                            style={{ background: 'none', border: 'none', color: GREEN, cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}
                          >
                            Submit your first lead
                          </button>
                        </>
                      ) : `No ${leadTab} leads.`}
                    </div>
                  ) : (
                    filteredLeads.map(lead => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onView={() => navigate('/introducer/referrals?open=' + lead.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div>
              {/* Activity Feed */}
              <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid #f1f5f9` }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: NAVY }}>Recent Activity</h2>
                </div>
                <div style={{ padding: '4px 16px 12px' }}>
                  <ActivityFeed items={activity} />
                </div>
              </div>

              {/* Quick Nav */}
              <QuickNavCard items={quickNavItems} navigate={navigate} />

              {/* Team Summary */}
              <TeamSummaryPanel company={company} level={level} navigate={navigate} />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
